import { useDialogContext, useStoreState } from "@ariakit/react";
import { LetterheadSubmitError, useForm } from "@indietabletop/appkit";
import { toast } from "react-hot-toast";
import { database } from "../../database.ts";
import { EditMode } from "../../engine/EditMode.ts";
import { caps } from "../fmt.ts";
import { FormDialog, FormDialogActions } from "../FormDialog/FormDialog.tsx";
import { FormDialogSubmitButton } from "../forms/DialogSubmitButton.tsx";
import { FormOption, FormOptions } from "../forms/FormTextField.tsx";
import { PremiumContentNote } from "../PremiumContentNote/PremiumContentNote.tsx";
import { ItemToBeRemovedWarning } from "./FormNote.tsx";
import { useGitContext } from "./ModelContext.tsx";
import { RichContent } from "./PremiumContent.tsx";
import { PreviewBanner } from "./StashPreviewBanner.tsx";
import { formOption } from "./style.css.ts";

export function ChooseGearDialog() {
  const git = useGitContext();
  const { gang, itemCostLimit, ruleset } = git;
  const { isCampaignMode } = gang.mode;
  const costModifier = isCampaignMode
    ? ruleset.constants.campaignModeCostMultiplier
    : 1;
  const isFilteredList = itemCostLimit !== Infinity;
  const rulesetGear = ruleset.gear.toArray();
  const initialGearIds = git.gear
    .filter((r) => r.isSuccess)
    .map((r) => r.value.id);
  const canRemoveExisting = git.can(EditMode.REMOVE_DIRECTLY, "gear");

  const gear = isFilteredList
    ? rulesetGear.filter(
        (w) => w.cost <= git.itemCostLimit || initialGearIds.includes(w.id),
      )
    : rulesetGear;

  const dialog = useDialogContext();

  const { form, submitName } = useForm({
    defaultValues: {
      gearIds: initialGearIds,
    },

    async onSubmit({ values }) {
      const gear = values.gearIds.map((id) => ({ id }));
      const nextGit = git.addGear(gear);
      return await database.gangs.updateOne(nextGit.gang.data);
    },

    onSuccess() {
      dialog?.hide();
      toast("Saved");
    },
  });

  const selectedGearIds = useStoreState(form, (s) => s.values.gearIds);
  const draftGang = git.addGear(selectedGearIds.map((id) => ({ id }))).gang;

  return (
    <FormDialog
      overline="Grandpa Gunnock's"
      heading="Fancy Gear Catalogue"
      form={form}
    >
      <PreviewBanner draftGang={draftGang} where="in the shop" />

      <PremiumContentNote />

      {isFilteredList && (
        <p style={{ textAlign: "center", lineHeight: 1.4 }}>
          Only showing weapons available for this git&apos;s type and
          gang&apos;s build mode.
        </p>
      )}

      <FormOptions label="Fancy Gear">
        {gear.map((g) => {
          const willBeLost =
            isCampaignMode &&
            initialGearIds.includes(g.id) &&
            !selectedGearIds.includes(g.id);

          return (
            <FormOption
              key={g.id}
              type="checkbox"
              name={form.names.gearIds}
              value={g.id}
              label={g.name}
              info={caps(g.cost * costModifier)}
              disabled={!canRemoveExisting && initialGearIds.includes(g.id)}
              description={
                <>
                  <RichContent>{g.description}</RichContent>

                  {willBeLost && (
                    <div className={formOption.extras}>
                      <ItemToBeRemovedWarning what="GEAR" />
                    </div>
                  )}
                </>
              }
            />
          );
        })}
      </FormOptions>

      <FormDialogActions>
        <LetterheadSubmitError name={submitName} />

        <FormDialogSubmitButton>Save</FormDialogSubmitButton>
      </FormDialogActions>
    </FormDialog>
  );
}
